import React from "react";
import { useParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Info } from "lucide-react";

const UPDATED = "Giugno 2026";

const DOCS = {
  privacy: {
    title: "Privacy Policy",
    intro:
      "La presente informativa descrive come Cristalda Experience (Michelangela Trimigno & C. S.a.s.) tratta i dati personali degli utenti ai sensi del Regolamento (UE) 2016/679 (GDPR) e del D.Lgs. 196/2003 e s.m.i.",
    sections: [
      {
        h: "1. Titolare del trattamento",
        p: [
          "Titolare del trattamento è Michelangela Trimigno & C. S.a.s. (\"Cristalda Experience\"), con sede in Centro Storico di Vieste (FG), Puglia, Italia.",
          "Per qualsiasi richiesta relativa ai dati personali è possibile scrivere a privacy@cristaldaexperience.it.",
        ],
      },
      {
        h: "2. Dati raccolti",
        p: [
          "Raccogliamo esclusivamente i dati necessari alla fornitura del servizio: nome, cognome, indirizzo email, numero di telefono, lingua e paese di provenienza, nonché i dettagli delle prenotazioni effettuate.",
          "I dati di pagamento (numeri di carta) NON vengono mai raccolti né conservati da Cristalda Experience: sono gestiti direttamente dal fornitore di pagamento certificato PCI-DSS (Stripe).",
        ],
      },
      {
        h: "3. Finalità e base giuridica",
        p: [
          "Trattiamo i dati per: (a) gestire prenotazioni e voucher (esecuzione del contratto); (b) adempimenti fiscali e legali (obbligo di legge); (c) assistenza clienti (legittimo interesse); (d) comunicazioni marketing, solo previo consenso esplicito e revocabile in qualsiasi momento.",
        ],
      },
      {
        h: "4. Conservazione",
        p: [
          "I dati vengono conservati per il tempo strettamente necessario alle finalità indicate e nel rispetto dei termini di legge (in particolare gli obblighi contabili e fiscali decennali).",
        ],
      },
      {
        h: "5. Comunicazione a terzi",
        p: [
          "I dati possono essere comunicati esclusivamente agli operatori locali coinvolti nell'erogazione dell'esperienza prenotata e ai fornitori tecnici (hosting, pagamenti) che agiscono come responsabili del trattamento. I dati non vengono ceduti o venduti a terzi per finalità proprie.",
        ],
      },
      {
        h: "6. Diritti dell'interessato",
        p: [
          "In qualsiasi momento è possibile esercitare i diritti di accesso, rettifica, cancellazione, limitazione, portabilità e opposizione, nonché revocare il consenso marketing, scrivendo a privacy@cristaldaexperience.it. È inoltre possibile proporre reclamo al Garante per la protezione dei dati personali.",
        ],
      },
    ],
  },
  cookie: {
    title: "Cookie Policy",
    intro:
      "Questo sito utilizza cookie e tecnologie simili per garantire il corretto funzionamento della piattaforma e, previo consenso, per finalità di analisi e marketing.",
    sections: [
      {
        h: "1. Cosa sono i cookie",
        p: [
          "I cookie sono piccoli file di testo che i siti visitati inviano al dispositivo dell'utente, dove vengono memorizzati per essere ritrasmessi agli stessi siti alla visita successiva.",
        ],
      },
      {
        h: "2. Cookie tecnici essenziali",
        p: [
          "Sono necessari al funzionamento del sito (gestione della sessione di navigazione, autenticazione, carrello, preferenza della lingua) e non richiedono il consenso dell'utente. Senza questi cookie il sito non può funzionare correttamente.",
        ],
      },
      {
        h: "3. Cookie di analisi e marketing",
        p: [
          "Vengono utilizzati, solo previo consenso esplicito dell'utente, per comprendere l'utilizzo del sito e per finalità promozionali. Questi cookie non essenziali NON vengono attivati prima del consenso prestato tramite il banner.",
        ],
      },
      {
        h: "4. Gestione delle preferenze",
        p: [
          "È possibile accettare o rifiutare i cookie non essenziali tramite il banner mostrato alla prima visita. In qualsiasi momento è possibile modificare la scelta cancellando i cookie dalle impostazioni del proprio browser.",
        ],
      },
    ],
  },
  termini: {
    title: "Termini & Condizioni",
    intro:
      "I presenti Termini & Condizioni disciplinano l'utilizzo della piattaforma Cristalda Experience e i rapporti tra l'utente, la piattaforma e gli operatori locali.",
    sections: [
      {
        h: "1. Ruolo della piattaforma",
        p: [
          "Cristalda Experience è un marketplace che mette in contatto i clienti con operatori turistici locali indipendenti, i quali erogano direttamente le esperienze prenotate. Cristalda Experience agisce come intermediario tecnico e commerciale.",
        ],
      },
      {
        h: "2. Prenotazioni e pagamenti",
        p: [
          "La prenotazione si perfeziona con la conferma e l'emissione del voucher. Il pagamento è gestito tramite fornitore certificato (Stripe). I prezzi indicati sono comprensivi delle imposte applicabili, salvo diversa indicazione.",
        ],
      },
      {
        h: "3. Cancellazioni e politiche meteo",
        p: [
          "Le condizioni di cancellazione, i requisiti di partecipazione e le politiche in caso di maltempo sono indicate su ciascuna esperienza e definite dal singolo operatore. In caso di annullamento da parte dell'operatore si applicano riprogrammazione o rimborso secondo quanto previsto.",
        ],
      },
      {
        h: "4. Responsabilità",
        p: [
          "L'operatore locale è responsabile dell'erogazione dell'esperienza e del rispetto delle normative di sicurezza applicabili. Cristalda Experience non risponde di eventi imputabili all'operatore o a cause di forza maggiore.",
        ],
      },
      {
        h: "5. Legge applicabile",
        p: [
          "I presenti Termini sono regolati dalla legge italiana. Per ogni controversia è competente il foro del consumatore, ove applicabile.",
        ],
      },
    ],
  },
};

export default function Legal() {
  const { doc } = useParams();
  const content = DOCS[doc] || DOCS.privacy;
  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14" data-testid={`legal-${doc || "privacy"}`}>
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight text-slate-900">{content.title}</h1>
        <p className="text-sm text-slate-400 mt-2">Ultimo aggiornamento: {UPDATED}</p>
        <p className="mt-6 text-slate-600 leading-relaxed">{content.intro}</p>
        <div className="mt-8 space-y-7">
          {content.sections.map((s) => (
            <section key={s.h}>
              <h2 className="text-lg font-heading font-semibold text-slate-900">{s.h}</h2>
              <div className="mt-2 space-y-3 text-slate-600 leading-relaxed">
                {s.p.map((para, i) => <p key={`${s.h}-${i}`}>{para}</p>)}
              </div>
            </section>
          ))}
        </div>
        <div className="mt-10 rounded-xl bg-slate-50 text-slate-500 text-xs px-4 py-3 flex items-start gap-2">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Documento redatto a fini informativi. Si consiglia una verifica finale da parte di un consulente legale prima della pubblicazione definitiva.</span>
        </div>
      </div>
    </Layout>
  );
}
